import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect } from 'react';
import { FormHelperText, RadioGroup } from '@mui/material';

import { IUnregisteredStepFour } from '../unregistered-steps-types';
import { logData } from '../../../../../utils/debug.util';
import { hideModal } from '../../../../../store/slices/generalSlice';
import { RootState } from '../../../../../store/rootReducer';
import useGeocoding from '../../../../../hooks/useGeocoding/useGeocoding';
import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import { FormLayout } from '../../../../../global-styles/layout';
import { theme } from '../../../../../global-styles/colors';
import { getErrorMessage } from '../../../../../constants/errorMessages';
import { BodySmallText, Heading4Text } from '../../../../../components/typography/typography';
import Radio from '../../../../../components/radio/radio';
import { LocationOption } from '../../../../../components/google-maps/google-maps-types';
import GoogleMaps, { DEFAULT_ADDRESS } from '../../../../../components/google-maps/GoogleMaps';
import ModalContainer from '../../../../../components/common/modal-container/modal-container';
import ControlledInput from '../../../../../components/common/forms/ControlledInput';
import ControlledAutoComplete from '../../../../../components/common/forms/ControlledAutoComplete';
import CardSubsection from '../../../../../components/card-subsection/card-subsection';
import { locationIcons } from '../../../../../assets/icons/icons';
import { fetchDetails } from '../../../../../api/fetchLocation';
import { UnregisteredStep4Style } from './unregistered-4-style';

type Option = {
  label: string;
  value: string;
};

const isValidOption = (option: unknown): option is Option => {
  return !!option && (option as Option).value !== undefined;
};

const UnregisteredStep4: React.FC<IUnregisteredStepFour> = ({ form, coords }) => {
  const { t } = useTranslation(['common', 'auth']);
  const dispatch = useDispatch();
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | undefined>();
  const [iconLocation, setIconLocation] = useState(locationIcons['00000016']);
  const providerCode = useSelector((state: RootState) => state.general.provider);

  useEffect(() => {
    if (providerCode) {
      setIconLocation(locationIcons[providerCode as keyof typeof locationIcons]);
    }
  }, [providerCode]);

  // Form
  const {
    control,
    formState: { errors },
    register,
    watch,
    getValues,
    setValue,
    unregister,
    clearErrors,
  } = form;
  const [undergroundParking, externalRoad] = watch(['undergroundParking', 'externalRoad']);
  const [latitude, longitude, locationDescription] = getValues(['latitude', 'longitude', 'locationDescription']);

  const modal = useSelector((state: RootState) => state.general.modal);

  useEffect(() => {
    if (providerCode) {
      setIconLocation(locationIcons[providerCode as keyof typeof locationIcons]);
    }
  }, [providerCode]);

  // Coords logic
  const [locationSearch, setLocationSearch] = useState<string>(() => {
    if (coords || (latitude && longitude)) {
      return `${coords?.latitude ?? latitude}, ${coords?.longitude ?? longitude}`;
    }
    return '';
  });

  const newCoords = useDebounce(locationSearch, 1000);
  const { predictions, locationData } = useGeocoding({
    input: newCoords,
    queryOptions: { refetchOnWindowFocus: false },
    location: { lat: coords?.latitude ?? latitude, lng: coords?.longitude ?? longitude },
  });

  const [selectedLocation, setSelectedLocation] = useState<LocationOption | undefined>(() => {
    if (coords || (latitude && longitude)) {
      return { label: '', value: [coords?.latitude ?? latitude, coords?.longitude ?? longitude] };
    }

    return undefined;
  });

  const [selectedDefaultAddress, setSelectedDefaultAddress] = useState<boolean>(false);

  const locationAddress = useMemo(() => {
    logData('locationAddress', {
      selectedLocation,
      locationData,
      coords,
      locationDescription,
    });

    let addressString = '';

    if (selectedLocation?.formattedAddress) {
      addressString = selectedLocation?.formattedAddress;
    } else if (selectedLocation?.label) {
      addressString = selectedLocation?.label;
    } else if (locationData?.results?.[0].formatted_address && coords) {
      addressString = locationData.results[0].formatted_address;
    } else {
      addressString = locationDescription;
    }

    setSelectedDefaultAddress(addressString === DEFAULT_ADDRESS);

    logData(
      // eslint-disable-next-line max-len
      `locationAddress final: ${addressString === DEFAULT_ADDRESS ? 'DEFAULT_ADDRESS' : 'VALID_ADDRESS'} ${addressString}`,
    );
    return addressString ?? '';
  }, [coords, locationData, locationDescription, selectedLocation]);

  useEffect(() => {
    if (selectedLocation) {
      clearErrors('search');
      setValue('latitude', selectedLocation.value[0]);
      setValue('longitude', selectedLocation.value[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  useEffect(() => {
    if (externalRoad) unregister('undergroundParking');
  }, [externalRoad, unregister]);

  useEffect(() => {
    return () => {
      if (timeoutId) globalThis.clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const setNewLocation = (location: LocationOption) => {
    setSelectedLocation(location);
  };

  return (
    <UnregisteredStep4Style>
      <Heading4Text className="m-12px" weight={600}>
        {t('auth:unregistered.step-3.title')}
      </Heading4Text>
      <BodySmallText color={theme.type.secondary} className="m-24px">
        {t('auth:unregistered.step-3.text')}
      </BodySmallText>{' '}
      <div className="search-container">
        <ControlledAutoComplete
          data-cy={'place-search'}
          options={
            predictions?.map((place: google.maps.places.PlacePrediction) => ({
              label: place.text.text,
              value: place.placeId,
            })) || []
          }
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') {
              setLocationSearch(value);
            }
          }}
          label={t('common:label-inputs.search')}
          onChange={(_, option) => {
            if (isValidOption(option)) {
              logData('Y) fetchDetails', { option });
              fetchDetails(option.value, ['location', 'formattedAddress']).then((details) => {
                logData('Y) fetchDetails result', { details });
                const lat = details?.location?.lat();
                const lng = details?.location?.lng();
                if (lat && lng) {
                  const location: LocationOption = {
                    label: option.label,
                    value: [lat, lng],
                    formattedAddress: details?.formattedAddress || undefined,
                  };
                  setSelectedLocation(location);
                }
              });
            }
          }}
          freeSolo
          filterOptions={(options) => {
            return options;
          }}
          noOptionsText={t('common:common-text.no-options')}
          controllerProps={{
            name: 'search',
            control: control,
            rules: {
              required: !selectedLocation,
            },
          }}
        />{' '}
        <FormHelperText className="error-block" error>
          {getErrorMessage(t, errors.search?.type)}
        </FormHelperText>
      </div>
      <div className="location-description">
        {locationAddress.length > 0 && <img src={iconLocation} alt="location" />}
        <div>{locationAddress.length > 0 && <BodySmallText weight={600}>{locationAddress}</BodySmallText>}</div>
      </div>
      <div className="map-container m-24px">
        <GoogleMaps
          coords={selectedLocation?.value}
          zoom={13}
          updateLocationFn={setNewLocation}
          showCenter={true}
          autoLocate={true}
        />
      </div>
      <FormLayout>
        <ControlledInput
          inputProps={{
            multiline: true,
            label: t('auth:unregistered.step-3.location-information-placeholder'),
            'data-cy': 'username',
            variant: 'filled',
            inputProps: {
              maxLength: 250,
            },
          }}
          controllerProps={{
            control,
            name: 'locationInformation',
          }}
        />
        <CardSubsection label={t('auth:unregistered.step-3.place')} className="radio-container">
          <RadioGroup>
            <Radio
              id={'externalRoad-1'}
              dataCy={'externalRoad-1'}
              active={externalRoad === true}
              label={t('common:label-inputs.highway')}
              value="true"
              onClick={(e) => {
                setValue('externalRoad', e.currentTarget.checked);
              }}
              className="radio-container"
              register={register('externalRoad', {
                validate: { required: (value) => typeof value === 'boolean' },
              })}
            />
            <Radio
              id={'externalRoad-2'}
              dataCy={'externalRoad-2'}
              active={externalRoad === false}
              label={t('common:label-inputs.city')}
              value="false"
              onClick={(e) => {
                setValue('externalRoad', !e.currentTarget.checked);
                const newTimeoutId = setTimeout(() => {
                  window.scrollTo(0, window.scrollY + 250);
                }, 200);
                setTimeoutId(newTimeoutId);
              }}
              className="radio-item"
              register={register('externalRoad', {
                validate: { required: (value) => typeof value === 'boolean' },
              })}
            />
          </RadioGroup>
          <FormHelperText error>{getErrorMessage(t, errors.externalRoad?.type)}</FormHelperText>
        </CardSubsection>
        {externalRoad === false && (
          <CardSubsection label={t('auth:unregistered.step-3.sub')} className="radio-container">
            <RadioGroup>
              <Radio
                id={'undergroundParking-1'}
                dataCy={'undergroundParking-1'}
                active={undergroundParking === true}
                label={t('common:label-inputs.affirmative-response')}
                value="true"
                register={register('undergroundParking', {
                  validate: { required: (value) => typeof value === 'boolean' },
                })}
                onClick={(e) => setValue('undergroundParking', e.currentTarget.checked)}
              />
              <Radio
                id={'undergroundParking-2'}
                dataCy={'undergroundParking-2'}
                active={undergroundParking === false}
                label={t('common:label-inputs.negative-response')}
                value="false"
                register={register('undergroundParking', {
                  validate: { required: (value) => typeof value === 'boolean' },
                })}
                onClick={(e) => setValue('undergroundParking', !e.currentTarget.checked)}
              />
            </RadioGroup>
            <FormHelperText error>{getErrorMessage(t, errors.undergroundParking?.type)}</FormHelperText>
          </CardSubsection>
        )}
      </FormLayout>
      {/* ModalConfirm Position con dirección válida */}
      <ModalContainer
        fullScreen
        buttons={[
          {
            className: 'primary',
            onClick: () => {
              setValue('locationDescription', locationAddress);
              if (modal.onConfirm) modal.onConfirm(selectedLocation?.value);
            },
            label: t('auth:unregistered.step-3.modal.next'),
            dataCy: 'confirm-position-btn',
          },
          {
            className: 'secondary',
            onClick: () => {
              dispatch(hideModal());
            },
            label: t('auth:unregistered.step-3.modal.modify-location'),
            dataCy: 'edit-phone-cancel-button',
          },
        ]}
        active={modal.name === 'confirm-location' && selectedDefaultAddress === false}
        onClose={() => {
          dispatch(hideModal());
        }}
        title={t('auth:unregistered.step-3.modal.title')}
        text={t('auth:unregistered.step-3.modal.text')}
        dataCy="edit-phone-number-modal"
      >
        <div className="location-description">
          {locationAddress.length > 0 && <BodySmallText weight={600}>{locationAddress}</BodySmallText>}
        </div>
        <GoogleMaps coords={selectedLocation?.value} zoom={13} showLocation />
        <div className="location-description m-t-16px">
          <BodySmallText weight={600}>
            {t('auth:unregistered.step-3.place')}:{' '}
            {externalRoad ? t('common:label-inputs.highway') : t('common:label-inputs.city')}
          </BodySmallText>
        </div>
        {externalRoad === false && (
          <div className="location-description m-t-16px">
            <BodySmallText weight={600}>
              {t('auth:unregistered.step-3.sub')}:{' '}
              {undergroundParking
                ? t('common:label-inputs.affirmative-response')
                : t('common:label-inputs.negative-response')}
            </BodySmallText>
          </div>
        )}
      </ModalContainer>
      {/* ModalConfirm Position con dirección no válida */}
      <ModalContainer
        buttons={[
          {
            className: 'primary',
            onClick: () => {
              dispatch(hideModal());
            },
            label: t('auth:unregistered.step-3.modal.modify-location'),
            dataCy: 'modify-location-btn',
          },
        ]}
        active={modal.name === 'confirm-location' && selectedDefaultAddress === true}
        onClose={() => {
          dispatch(hideModal());
        }}
        title={t('auth:unregistered.step-3.invalid-location-modal.title')}
        text={t('auth:unregistered.step-3.invalid-location-modal.text')}
        dataCy="edit-phone-number-modal"
      />
    </UnregisteredStep4Style>
  );
};

export default UnregisteredStep4;
